<script setup lang="ts">
const { t } = useTexts();
const { brandContacts } = useFeatureFlagsNew();
const { openSupportChat, hasLiveSupport } = useLiveSupport();

const hasAnySacContact = computed(() => {
  if (!brandContacts.value || !brandContacts.value.sac) {
    return false;
  }

  return Object.values(brandContacts.value.sac).some((value) => value);
});

const hasAnyCustomerServiceContact = computed(() => {
  if (!brandContacts.value || !brandContacts.value.customer_service) {
    return false;
  }

  return Object.values(brandContacts.value.customer_service).some(
    (value) => value,
  );
});

const hasAnyComplaintsContact = computed(() => {
  if (!brandContacts.value || !brandContacts.value.complaints) {
    return false;
  }

  return Object.values(brandContacts.value.complaints).some((value) => value);
});

const hasAnySupportContact = computed(() => {
  if (!brandContacts.value || !brandContacts.value.support) {
    return false;
  }

  return Object.values(brandContacts.value.support).some((value) => value);
});

const hasAnyGeneralContact = computed(() => {
  if (!brandContacts.value || !brandContacts.value.general) {
    return false;
  }

  return Object.values(brandContacts.value.general).some((value) => value);
});

function getLinkText(link: string) {
  try {
    const url = new URL(link);

    if (url.hostname) {
      return url.hostname;
    }

    return t('help_page.access_link');
  } catch (error) {
    return t('help_page.access_link');
  }
}
</script>

<template>
  <div :class="$style.help">
    <h1>Central de Ajuda: suporte e guia de uso Apostatudo</h1>

    <p>
      {{ $t('help_page.text_part_1') }}
    </p>

    <p>
      {{ $t('help_page.text_part_2') }}
    </p>

    <p>
      {{ $t('help_page.text_part_3') }}
    </p>

    <p>
      {{ $t('help_page.text_part_4') }}
    </p>

    <p>
      {{ $t('help_page.text_part_5') }}
    </p>

    <ul v-if="hasAnySacContact">
      <li>
        <b>{{ $t('help_page.sac') }}</b>
      </li>
      <li v-if="brandContacts.sac.phone">
        {{ $t('help_page.phone') }}:
        <a :href="`tel:${brandContacts.sac.phone}`">
          {{ brandContacts.sac.phone }}
        </a>
      </li>
      <li v-if="brandContacts.sac.email">
        {{ $t('help_page.email') }}:
        <a :href="`mailto:${brandContacts.sac.email}`">
          {{ brandContacts.sac.email }}
        </a>
      </li>
      <li v-if="brandContacts.sac.link">
        {{ $t('help_page.link') }}:
        <a
          :href="brandContacts.sac.link"
          target="_blank"
        >
          {{ getLinkText(brandContacts.sac.link) }}
        </a>
      </li>
    </ul>

    <ul v-if="hasAnyCustomerServiceContact">
      <li>
        <b>{{ $t('help_page.ouvidoria') }}</b>
      </li>
      <li v-if="brandContacts.customer_service.phone">
        {{ $t('help_page.phone') }}:
        <a :href="`tel:${brandContacts.customer_service.phone}`">
          {{ brandContacts.customer_service.phone }}
        </a>
      </li>
      <li v-if="brandContacts.customer_service.email">
        {{ $t('help_page.email') }}:
        <a :href="`mailto:${brandContacts.customer_service.email}`">
          {{ brandContacts.customer_service.email }}
        </a>
      </li>
      <li v-if="brandContacts.customer_service.link">
        {{ $t('help_page.link') }}:
        <a
          :href="brandContacts.customer_service.link"
          target="_blank"
        >
          {{ getLinkText(brandContacts.customer_service.link) }}
        </a>
      </li>
    </ul>

    <ul v-if="hasAnyComplaintsContact">
      <li>
        <b>{{ $t('help_page.complaints') }}</b>
      </li>
      <li v-if="brandContacts.complaints.phone">
        {{ $t('help_page.phone') }}:
        <a :href="`tel:${brandContacts.complaints.phone}`">
          {{ brandContacts.complaints.phone }}
        </a>
      </li>
      <li v-if="brandContacts.complaints.email">
        {{ $t('help_page.email') }}:
        <a :href="`mailto:${brandContacts.complaints.email}`">
          {{ brandContacts.complaints.email }}
        </a>
      </li>
      <li v-if="brandContacts.complaints.link">
        {{ $t('help_page.link') }}:
        <a
          :href="brandContacts.complaints.link"
          target="_blank"
        >
          {{ getLinkText(brandContacts.complaints.link) }}
        </a>
      </li>
    </ul>

    <ul v-if="hasAnySupportContact">
      <li>
        <b>{{ $t('help_page.support') }}</b>
      </li>
      <li v-if="brandContacts.support.phone">
        {{ $t('help_page.phone') }}:
        <a :href="`tel:${brandContacts.support.phone}`">
          {{ brandContacts.support.phone }}
        </a>
      </li>
      <li v-if="brandContacts.support.email">
        {{ $t('help_page.email') }}:
        <a :href="`mailto:${brandContacts.support.email}`">
          {{ brandContacts.support.email }}
        </a>
      </li>
      <li v-if="brandContacts.support.link">
        {{ $t('help_page.link') }}:
        <a
          :href="brandContacts.support.link"
          target="_blank"
        >
          {{ getLinkText(brandContacts.support.link) }}
        </a>
      </li>
    </ul>

    <ul v-if="hasAnyGeneralContact">
      <li>
        <b>{{ $t('help_page.general_support') }}</b>
      </li>
      <li v-if="brandContacts.general.phone">
        {{ $t('help_page.phone') }}:
        <a :href="`tel:${brandContacts.general.phone}`">
          {{ brandContacts.general.phone }}
        </a>
      </li>
      <li v-if="brandContacts.general.email">
        {{ $t('help_page.email') }}:
        <a :href="`mailto:${brandContacts.general.email}`">
          {{ brandContacts.general.email }}
        </a>
      </li>
      <li v-if="brandContacts.general.link">
        {{ $t('help_page.link') }}:
        <a
          :href="brandContacts.general.link"
          target="_blank"
        >
          {{ getLinkText(brandContacts.general.link) }}
        </a>
      </li>
      <li v-if="hasLiveSupport">
        {{ $t('help_page.live_support') }}:
        <button @click="openSupportChat">
          {{ $t('help_page.open_live_support') }}
        </button>
      </li>
    </ul>
  </div>
</template>

<style module lang="postcss">
.help {
  @apply relative px-8 pt-2 pb-12 flex flex-col gap-5 text-auth-texts text-sm md:pb-10 md:h-[618px] md:overflow-auto;

  ul {
    @apply border-solid border-t border-auth-texts/10 pt-5 px-2;

    li {
      @apply py-1;
    }

    b {
      @apply text-auth-texts/50;
    }

    a,
    button {
      @apply text-primary font-bold;
    }
  }
}
</style>
